import { AxiosResponse } from 'axios';
import { takeLatest, call, put, all, select, delay } from 'redux-saga/effects';
import { toast } from 'react-hot-toast';
import { OrganizationItem, OrganizationsResponse, SwitchOrgResponse } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import { selectIdToken, unauthorized } from '../../auth/redux/slice';
import authSlice from '../../auth/redux/slice';
import orgSwitcherSlice, { loadSwitcherOrganizations, selectSwitcherOrganizations, switchOrg } from './slice';

export abstract class OrgSwitcherSagas {
    public static* run() {
        try {
            yield all([
                takeLatest(loadSwitcherOrganizations.type, OrgSwitcherSagas.loadOrganizations),
                takeLatest(switchOrg.type, OrgSwitcherSagas.switchOrg),
            ]);
        }
        catch (e: any) {
            console.log(`[OrgSwitcherSagas:run] the following exception has occurred`, e);
        }
    }

    private static* loadOrganizations() {
        try {
            yield put(orgSwitcherSlice.actions.setOrganizationsState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orgsResponse: AxiosResponse<OrganizationsResponse> = yield call(
                { context: client, fn: client.allOrganizations },
            );

            if (orgsResponse.status === 401) {
                yield put(unauthorized(undefined));
                return;
            }

            if (orgsResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(orgSwitcherSlice.actions.setOrganizations(orgsResponse.data));
        }
        catch (e: any) {
            console.log(`[OrgSwitcherSagas:loadOrganizations] the following exception has occurred`, e);
        }
        finally {
            yield put(orgSwitcherSlice.actions.setOrganizationsState('READY'));
        }
    }

    // A local dev/fast-network round trip can complete in well under 100ms, which made the loading
    // backdrop read as a jarring flash rather than a deliberate transition. Enforcing this floor
    // (only on the success path - an error surfaces its toast immediately, no need to delay that)
    // gives the user something perceptible to read as "switching," even when the request itself
    // was effectively instant.
    private static readonly MIN_BUSY_MS = 500;

    private static* switchOrg(action: ReturnType<typeof switchOrg>) {
        const { payload: { orgId } } = action;
        const startedAt = Date.now();
        try {
            yield put(orgSwitcherSlice.actions.setSwitchState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const switchResponse: AxiosResponse<SwitchOrgResponse> = yield call(
                { context: client, fn: client.switchOrg },
                { orgId },
            );

            if (switchResponse.status === 401) {
                yield put(unauthorized(undefined));
                return;
            }

            if (switchResponse.status !== 200)
                throw new Error("Error communicating with Api");

            const { token } = switchResponse.data;

            // Reusing the exact same setAuth reducer login uses - every selector that reads
            // "current org" (selectUserOrgId/selectUserOrgName/etc.) is derived from the decoded
            // JWT, so switching orgs this way makes every other org-scoped part of the app (Pre-Buy
            // gating, dashboard stats, order creation) reflect the new org automatically.
            yield put(authSlice.actions.setAuth({ token }));

            // Drives the "you're now viewing X, here's what you can do" confirmation dialog - a
            // plain toast was too easy to miss for a change this consequential (everything the user
            // sees/does next is scoped to this org), so this surfaces as a dialog instead. Pulled
            // from the already-loaded org list rather than the switch response, since it carries
            // fields (preBuyEnabled, etc.) the dialog needs to decide which shortcuts to offer.
            const organizations: OrganizationItem[] = yield select(selectSwitcherOrganizations);
            const switchedOrg = organizations.find(o => o.orgId === orgId);
            yield put(orgSwitcherSlice.actions.setLastSwitchedOrg(switchedOrg));

            const elapsedMs = Date.now() - startedAt;
            if (elapsedMs < OrgSwitcherSagas.MIN_BUSY_MS)
                yield delay(OrgSwitcherSagas.MIN_BUSY_MS - elapsedMs);
        }
        catch (e: any) {
            toast.error('Could not switch organization, please try again.');
            console.log(`[OrgSwitcherSagas:switchOrg] the following exception has occurred`, e);
        }
        finally {
            yield put(orgSwitcherSlice.actions.setSwitchState('READY'));
        }
    }
}
